import {type AiPromptBox, AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isUserPremium} from '@Libraries/user.library';
import {getUrlForVoicePreset} from '@Libraries/text-to-speech.library';
import {getAssetUrl} from '@Utils/s3.util';
import type {PlayableSubfilter, VoiceLanguageOption} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {LOADING_KEY_PREFIX, MAX_VOICE_PROMPT_LENGTH, PromptSubfilterType, VoiceLanguage} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import type {RosterVoiceConfig} from '@Panels/ai-voice-panel/ai-voice-panel-roster.data';
import {LANGUAGE_ROSTER, VOICE_CREDITS_GENERATIVE} from '@Panels/ai-voice-panel/ai-voice-panel-roster.data';

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/ai-text-to-speech/';
/** Bump when avatar art is re-uploaded so cached copies refresh (same pattern as getUrlForVoicePreset). */
const THUMBNAIL_CACHE_TS = 1789452881;
/** S3 assets-bucket folder holding the language flag SVGs (mirrored locally at repo/assets/flags/). */
const FLAGS_BASE_URL = 'flags/';
/** Bump when flag art is re-uploaded so cached copies refresh. */
const FLAGS_CACHE_TS = 1789113323;

interface RosterEntry {
  language: VoiceLanguage;
  voice: RosterVoiceConfig;
}

export const getVoiceLanguageFromString = (value: string): VoiceLanguage | null => {
  const languageValues: string[] = Object.values(VoiceLanguage);
  return languageValues.includes(value) ? (value as VoiceLanguage) : null;
};

export const getVoiceLanguageOptions = (): VoiceLanguageOption[] => {
  return Object.values(VoiceLanguage).map(getVoiceLanguageOption);
};

export const getVoiceLanguageOption = (language: VoiceLanguage): VoiceLanguageOption => {
  const config = LANGUAGE_ROSTER[language];
  return {
    language,
    flagUrl: getAssetUrl(`${FLAGS_BASE_URL}${config.flagFile}?ts=${FLAGS_CACHE_TS}`),
    displayName: window.i18next.t(config.nameKey),
  };
};

export const getDefaultVoiceIdForLanguage = (language: VoiceLanguage): number => {
  return LANGUAGE_ROSTER[language].defaultVoiceId;
};

/**
 * The voices the profile dropdown offers. English hides its Polly neural tier
 * (isHiddenFromPicker) because the generative and GPT voices sound better; lookups
 * by id stay on the full roster index, so hidden picks still resolve a name,
 * avatar, and price.
 */
export const getSelectableVoiceFiltersForLanguage = (language: VoiceLanguage): PlayableSubfilter[] => {
  return LANGUAGE_ROSTER[language].voices.filter((voice) => !voice.isHiddenFromPicker).map(toPlayableSubfilter);
};

export const getLanguageForVoiceId = (voiceId: number): VoiceLanguage | null => {
  return ROSTER_ENTRY_BY_VOICE_ID.get(voiceId)?.language ?? null;
};

export const getVoiceFilterById = (voiceId: number): PlayableSubfilter | null => {
  const entry = ROSTER_ENTRY_BY_VOICE_ID.get(voiceId);
  return entry === undefined ? null : toPlayableSubfilter(entry.voice);
};

export const getCreditCostForVoiceId = (voiceId: number): number => {
  return ROSTER_ENTRY_BY_VOICE_ID.get(voiceId)?.voice.credits ?? VOICE_CREDITS_GENERATIVE;
};

/**
 * Flat voice-id index over LANGUAGE_ROSTER. Built once so a single-voice lookup does not
 * rebuild a whole language's PlayableSubfilter list — each one costs an i18next lookup and
 * a getAssetUrl (a jQuery body read) per voice, and these lookups run on every panel render.
 */
const buildRosterIndex = (): Map<number, RosterEntry> => {
  const index = new Map<number, RosterEntry>();
  for (const language of Object.values(VoiceLanguage)) {
    for (const voice of LANGUAGE_ROSTER[language].voices) {
      index.set(voice.id, {language, voice});
    }
  }
  return index;
};

const ROSTER_ENTRY_BY_VOICE_ID = buildRosterIndex();

const toPlayableSubfilter = (config: RosterVoiceConfig): PlayableSubfilter => {
  return {
    id: config.id,
    name: config.name,
    type: PromptSubfilterType.PLAYABLE,
    soundtrack: getUrlForVoicePreset(config.id),
    description: window.i18next.t(config.descriptionKey),
    accent: config.accentKey ? window.i18next.t(config.accentKey) : undefined,
    avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}${config.avatarFile}?ts=${THUMBNAIL_CACHE_TS}`),
    credits: config.credits,
  };
};

export const getLoadingKeyForAiVoice = (panelId: string): string => {
  return `${LOADING_KEY_PREFIX}-${panelId}`;
};

/** Prompt-box options for the AI writer ("help me write") flows — free, so no credits pill. */
export const getWriterPromptBoxOptions = (): AiPromptBox => {
  return {...getPromptBoxOptions(0), creditsPill: undefined};
};

export const getPromptBoxOptions = (actionCost: number): AiPromptBox => {
  return {
    placeholder: window.i18next.t('pmwjs_text_to_speech_input_placeholder'),
    showInitialAnimation: false,
    size: AiPromptBoxSize.SMALL_EXTENDED,
    creditsPill: {
      visible: true,
      actionCost,
      remainingCredits: 0,
      showPremiumIcon: !isUserPremium(),
    },
    promptBoxState: PromptBoxState.IDLE,
    prompt: '',
    input: {
      hideScrollbar: false,
      maxPromptLength: MAX_VOICE_PROMPT_LENGTH,
    },
  };
};
